// A moment is rendered relative to now by walking RELATIVE_INTERVALS, a descending table of units,
// and taking the first that fits. The walk stops at the reading's floor, below which the reading is
// "Just now". A view also returns `expires`, the instant its rendering goes stale: the contract with
// TIMER_WHEEL, which wakes the component then.

import {type ReactNode} from "react";
import {ApiDateTime} from "platformed-client/types";
import T from "platformed-components/i18n/T";
import {toTimestamp} from "./date";

export type TimerId = ReturnType<typeof setTimeout>;

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;

export function ceilTime(minInterval: number, timestamp?: number): number {
  return Math.ceil((timestamp ?? Date.now()) / minInterval) * minInterval;
}

export function roundedTime(minInterval: number, timestamp?: number): number {
  return Math.round((timestamp ?? Date.now()) / minInterval) * minInterval;
}

type TimerWheelCallback = (ts: number) => void;

class TimerWheel {
  #callbacks = new Map<TimerWheelCallback, number>();
  #minTimestamp = Infinity;
  #timerId: TimerId | null = null;
  #minInterval: number;

  constructor(minInterval: number) {
    this.#minInterval = minInterval;
  }

  get minTimestamp(): number {
    return this.#minTimestamp;
  }
  set minTimestamp(value: number) {
    if (this.#timerId !== null) {
      clearTimeout(this.#timerId);
    }
    this.#minTimestamp = value;
    if (value !== Infinity) {
      const delay = Math.max(value - Date.now(), 0);
      this.#timerId = setTimeout(this.#update, delay);
    }
  }

  #update = () => {
    this.#timerId = null;
    const timestamp = roundedTime(this.#minInterval);
    const fs = [];
    let newMinTimestamp = Infinity;
    for (const [f, ts] of this.#callbacks) {
      if (ts <= timestamp) {
        fs.push(f);
        this.#callbacks.delete(f);
      } else {
        newMinTimestamp = Math.min(newMinTimestamp, ts);
      }
    }
    this.minTimestamp = newMinTimestamp;
    for (const f of fs) {
      try {
        f(timestamp);
      } catch (ex) {
        console.error(ex);
      }
    }
  };

  add(minInterval: number, timestamp: number, f: TimerWheelCallback): () => void {
    if (timestamp === Infinity) {
      return () => {};
    }
    timestamp = ceilTime(minInterval, timestamp);
    this.#callbacks.set(f, timestamp);

    if (timestamp < this.minTimestamp) {
      this.minTimestamp = timestamp;
    }

    return () => {
      this.#callbacks.delete(f);
    };
  }
}

export const TIMER_WHEEL = new TimerWheel(10.0);

export interface TimeView<T> {
  (timestamp: number): {current: T; expires: number};
  minInterval?: number;
}

const RELATIVE_INTERVALS = [
  [365 * DAY, "c.years_ago[enumerated]"],
  [30 * DAY, "c.months_ago[enumerated]"],
  [WEEK, "c.weeks_ago[enumerated]"],
  [DAY, "c.days_ago[enumerated]"],
  [HOUR, "c.hours_ago[enumerated]"],
  [MINUTE, "c.minutes_ago[enumerated]"],
  [SECOND, "c.seconds_ago[enumerated]"],
] as const;

/** `floor` is the finest unit a reading may use. */
export function relativeDateView(timestamp?: ApiDateTime | null, floor: number = SECOND): TimeView<ReactNode> {
  if (!timestamp) return () => ({current: null, expires: Infinity});
  return now => {
    const ts = toTimestamp(timestamp);
    const delta = now - ts;
    for (const [v, keyName] of RELATIVE_INTERVALS) {
      if (v < floor) break;
      if (delta >= v) {
        const n = Math.floor(delta / v);
        const expires = (n + 1) * v + ts;
        return {current: <T keyName={keyName} params={{n}} />, expires};
      }
    }
    return {current: <T keyName="c.Just_now" />, expires: ts + floor};
  };
}
